import { useEffect, useState } from "react";
import { manifestEntries, mergeLibrary, type ManifestEntry } from "./libraryMerge";
import { VIEWER_ORIGIN } from "./viewerOrigin";

export type { ManifestEntry } from "./libraryMerge";

/** Preset kinds the library API can serve. Others stay static-only.
 *  `stage` / `ground` / `scene` have NO static manifest — they exist only as
 *  curated presets, so their fallback is an empty list, not a 404. */
export type LibraryKind =
  | "env" | "gemEnv" | "background" | "stage" | "ground" | "scene"
  // the five material categories
  | "metal" | "gem" | "ceramic" | "pearl" | "other"
  // Workspace scene snapshots, saved by "Save current as Look". These have
  // existed as Preset documents since the Canvas chrome shipped and nothing
  // could read them back here — widening this union is a data-recovery step,
  // not just a feature.
  | "look"
  // On-body photographs for the try-on panel. Distinct from `background`:
  // both are backdrops, but one is a gradient and the other is a hand.
  | "onBody";

interface ApiPreset {
  _id: string;
  /** Absent on presets saved before the curated library — all workspace-owned. */
  scope?: "system" | "workspace";
  kind: string;
  name: string;
  fileUrl: string | null;
  thumbUrl: string | null;
  values?: Record<string, unknown>;
}

/**
 * An environment library lists FILES. `env` / `gemEnv` presets also hold the
 * old value-only snapshots (Scene › Saved Environments writes `env` rows with
 * no file), which used to arrive here as `file: ""` tiles that loaded nothing.
 * Those are dropped; every other kind keeps its rows (a curated `look` or
 * `scene` preset is legitimately file-less).
 */
export function keepApiRow(kind: string, p: { fileUrl: string | null | undefined }): boolean {
  if (kind === "env" || kind === "gemEnv") return typeof p.fileUrl === "string" && p.fileUrl.length > 0;
  return true;
}

/** Any URL with a scheme (https:, data:, blob:) or protocol-relative. */
const isAbsolute = (s: string) => /^([a-z][a-z\d+.-]*:|\/\/)/i.test(s);

/**
 * One request per static manifest per page. The five material grids each read
 * their category out of the same 450-entry iJewel manifest, and every grid
 * instance mounts all five — without this that file was fetched five times per
 * grid. A failed load is forgotten, so the next mount tries again.
 */
const staticLoads = new Map<string, Promise<ManifestEntry[]>>();
function loadStaticManifest(path: string): Promise<ManifestEntry[]> {
  let load = staticLoads.get(path);
  if (!load) {
    load = fetch(path).then(async (r) => {
      if (!r.ok) throw new Error(`${path}: HTTP ${r.status}`);
      return manifestEntries(await r.json());
    });
    load.catch(() => staticLoads.delete(path));
    staticLoads.set(path, load);
  }
  return load;
}

/**
 * Loads an asset library.
 *
 * Two sources, one shape, and the result is the UNION of both. `kind` opts into
 * the curated preset API (`GET /api/v1/presets?kind=…`, system ∪ workspace);
 * the static manifest in public/ is always loaded alongside it. Static entries
 * come first, and an API row that is a copy of one is dropped — see
 * `mergeLibrary` for what counts as a copy.
 *
 * It used to CHOOSE: a non-empty API response replaced the static list
 * entirely. That was invisible until the moment we curated presets for a kind
 * that also ships files — then the static entries silently vanished (26 gem
 * environments, 8 HDRIs, 18 gradients). Worse, it was invisible in exactly the
 * places we test: `/api/v1/presets` sits behind `authenticate`, so logged-out
 * sessions, `/dev/studio` and every headless harness kept the static list and
 * looked fine, while only signed-in customers lost the assets.
 *
 * Either source may be absent and that is normal, not an error: no `kind` ⇒
 * static only; API down or unauthenticated ⇒ static only; `stage`/`ground`/
 * `scene` ship no manifest at all ⇒ API only.
 *
 * A manifest may be a bare array or `{ items: [...] }`. One that files several
 * libraries together (iJewel's materials) is narrowed with `opts.category`.
 *
 * Use `urlFor` / `thumbFor` rather than joining `baseUrl` yourself — API
 * presets carry absolute URLs, static ones carry paths relative to the
 * manifest's folder, and an `origin: "viewer"` entry's file is on the viewer.
 */
export function useManifest<T extends ManifestEntry = ManifestEntry>(
  path: string | null,
  kind?: LibraryKind,
  /** Change to force a refetch — e.g. right after saving a new preset, which
   *  otherwise would not appear until the panel remounted. */
  reloadKey?: number,
  opts: {
    /** Keep only the static entries filed under this `category`. */
    category?: string;
  } = {},
): {
  items: T[];
  baseUrl: string;
  urlFor: (item: ManifestEntry) => string;
  thumbFor: (item: ManifestEntry) => string;
} {
  const [items, setItems] = useState<T[]>([]);
  const base = (path ?? "").replace(/\/manifest\.json$/, "");
  const baseUrl = `${window.location.origin}${base}`;
  const { category } = opts;

  useEffect(() => {
    let alive = true;

    // Each loader resolves to a list and never rejects — an absent source is a
    // normal outcome here, and a rejection would take the other source with it.
    const loadStatic = async (): Promise<T[]> => {
      if (!path) return [];
      try {
        const all = (await loadStaticManifest(path)) as T[];
        return category ? all.filter((e) => e.category === category) : all;
      } catch { return []; }
    };

    const loadApi = async (): Promise<T[]> => {
      if (!kind) return [];
      const token = (() => { try { return localStorage.getItem("auth_token"); } catch { return null; } })();
      const ws = (() => { try { return localStorage.getItem("active_workspace_id"); } catch { return null; } })();
      try {
        const r = await fetch(`/api/v1/presets?kind=${encodeURIComponent(kind)}`, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(ws ? { "X-Workspace-Id": ws } : {}),
          },
        });
        if (!r.ok) return [];
        const env = (await r.json()) as { data?: ApiPreset[] };
        const rows = (Array.isArray(env?.data) ? env.data : []).filter((p) => keepApiRow(kind, p));
        return rows.map((p) => ({
          // `values` FIRST: it is author-supplied JSON, and a preset carrying
          // its own `id` would otherwise clobber the one selection depends on.
          ...(p.values ?? {}),
          id: p._id,
          label: p.name,
          file: p.fileUrl ?? "",
          thumb: p.thumbUrl ?? "",
          owned: p.scope !== "system",
          // A row is already an absolute URL; it is never on the viewer.
          origin: undefined,
        })) as unknown as T[];
      } catch { return []; }
    };

    void Promise.all([loadStatic(), loadApi()]).then(([statics, api]) => {
      if (alive) setItems(mergeLibrary(statics, api));
    });

    return () => { alive = false; };
  }, [path, kind, baseUrl, reloadKey, category]);

  /** A path relative to `dir`, an origin-relative path, or an absolute URL as is. */
  const resolve = (v: string | undefined, origin: string, dir: string) =>
    !v ? "" : isAbsolute(v) ? v : v.startsWith("/") ? `${origin}${v}` : `${dir}/${v}`;
  return {
    items,
    baseUrl,
    urlFor: (i) =>
      i.origin === "viewer"
        ? resolve(i.file, VIEWER_ORIGIN, VIEWER_ORIGIN)
        : resolve(i.file, window.location.origin, baseUrl),
    thumbFor: (i) => resolve(i.thumb, window.location.origin, baseUrl),
  };
}
